/**
 * A log viewer: a row of track headers with their scales, over a ruled body.
 * Tracks are described by data so each log screen only supplies its own columns.
 */

export interface Scale {
  /** e.g. "Gamma Ray" */
  name?: string;
  /** left label, usually the minimum */
  min: string;
  unit?: string;
  /** right label, usually the maximum */
  max: string;
}

export interface Track {
  title: string;
  /** Renders the title rotated, as the narrow tracks on the portal do. */
  vertical?: boolean;
  width: number;
  scales?: Scale[];
  /** A block of colour painted in the body, for lithology-style tracks. */
  fill?: string;
}

interface Props {
  tracks: Track[];
  /** Depth ticks down the left edge. */
  depths?: number[];
  /** Floor for the plotting area; it grows to fill the panel. */
  minHeight?: number;
}

export function LogTrackPanel({ tracks, depths = [], minHeight = 260 }: Props) {
  return (
    <div className="logpanel">
      <div className="logpanel-scroll">
        <div className="logtracks">
          {tracks.map((track, i) => (
            <div className="logtrack" key={`${track.title}-${i}`} style={{ width: track.width }}>
              <div className={track.vertical ? 'logtrack-head is-vertical' : 'logtrack-head'}>
                <span className="logtrack-title">{track.title}</span>

                {track.scales?.map((s, j) => (
                  <div className="logscale" key={j}>
                    {s.name && <span className="logscale-name">{s.name}</span>}
                    <span className="logscale-row">
                      <em>{s.min}</em>
                      {s.unit && <i>{s.unit}</i>}
                      <em>{s.max}</em>
                    </span>
                  </div>
                ))}
              </div>

              <div className="logtrack-body" style={{ minHeight }}>
                {track.fill && <span className="logtrack-fill" style={{ background: track.fill }} />}
                {i === 0 &&
                  depths.map((d) => (
                    <span className="logtrack-depth" key={d} style={{ top: `${(d / (depths.at(-1) || 1)) * 92}%` }}>
                      {d}
                    </span>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="logpanel-hbar" aria-hidden="true">
        <span />
      </div>
    </div>
  );
}
